import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, CalendarDays, Clock, Phone, Map } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SEASON } from '../../shared/season.js';

type ContactItem = {
  Icon: LucideIcon;
  title: string;
  details: string[];
  bgColor: string;
  borderColor: string;
  iconBg: string;
};

const Contact: React.FC = () => {
  const contactInfo: ContactItem[] = [
    {
      Icon: MapPin,
      title: 'Atrašanās vieta',
      details: ['Stadions Viļānos', 'Viļāni, Latvija', 'LV-4650'],
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200',
      iconBg: 'from-primary-500 to-primary-600',
    },
    {
      Icon: CalendarDays,
      title: 'Nometnes datumi',
      details: [
        SEASON.weekRange,
        `Pusaudži ${SEASON.groups.teens.dates} · Bērni ${SEASON.groups.kids.dates}`,
        '2 + 2 dienas',
      ],
      bgColor: 'bg-secondary-50',
      borderColor: 'border-secondary-200',
      iconBg: 'from-secondary-500 to-secondary-600',
    },
    {
      Icon: Clock,
      title: 'Laiki',
      details: ['Sākums: 11:00', 'Beigas: 21:00', 'Ēdieni: 13:00–13:30 un 17:00–18:00'],
      bgColor: 'bg-accent-50',
      borderColor: 'border-accent-200',
      iconBg: 'from-accent-500 to-accent-600',
    },
    {
      Icon: Phone,
      title: 'Kontakti',
      details: ['Telefons: +371 24 904 140', 'E-pasts: ezaurins@pm.me', 'Organizatori pieejami'],
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      iconBg: 'from-emerald-500 to-emerald-600',
    },
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
            Kontakti un informācija
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ir jautājumi par nometni? Mēs esam šeit, lai palīdzētu — sazinies ar mums jebkurā laikā.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map(({ Icon, title, details, bgColor, borderColor, iconBg }, index) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className={`${bgColor} ${borderColor} border rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${iconBg} flex items-center justify-center shadow-md`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-lg text-gray-800 mb-3">{title}</h3>
              <div className="space-y-1">
                {details.map((detail, detailIndex) => (
                  <p key={detailIndex} className="text-gray-600 text-sm">
                    {detail}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Location Map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 bg-gray-100 rounded-3xl p-8"
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center gap-2">
            <Map className="w-6 h-6 text-primary-600" />
            Kā mūs atrast
          </h3>

          <div className="bg-white rounded-2xl overflow-hidden shadow-lg mb-6">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2175.4286471689544!2d26.92439458885!3d56.54388219999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46c20d01860684e5%3A0xe8aee91c03189c65!2sVi%C4%BC%C4%81nu%20stadions!5e0!3m2!1sen!2slv!4v1707844800000!5m2!1sen!2slv"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Viļānu stadions location"
            ></iframe>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
